import type { AgentDraft, AgentDraftResult } from '@af/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { IconSparkle } from '@/components/ui/icons';
import { Textarea } from '@/components/ui/input';
import { Banner } from '@/components/ui/misc';
import { call } from '@/lib/api';
import { AGENT_TEMPLATES, type AgentTemplate } from '@/lib/onboarding';

export type StarterChoice = AgentTemplate | 'blank';

/**
 * The two ways to start an agent: describe it (one cheap AI call on the platform key picks a
 * template and writes the profile) or pick a template. Nothing is created here; the caller shows
 * the result in the create form for review.
 */
export function AgentStarter({
  onTemplate,
  onDraft,
  onDescribe,
  initialDescription,
  showBlank = true,
}: {
  onTemplate: (c: StarterChoice) => void;
  /** Without it, "Design with AI" calls `onDescribe` instead (e.g. to continue on /agents/new). */
  onDraft?: (draft: AgentDraft, costUsd: number) => void;
  onDescribe?: (description: string) => void;
  initialDescription?: string;
  showBlank?: boolean;
}) {
  return (
    <div className="flex flex-col gap-5">
      <DescribeBox onDraft={onDraft} onDescribe={onDescribe} initialDescription={initialDescription} />
      <TemplateGrid onTemplate={onTemplate} showBlank={showBlank} />
    </div>
  );
}

export function DescribeBox({
  onDraft,
  onDescribe,
  initialDescription,
}: {
  onDraft?: (draft: AgentDraft, costUsd: number) => void;
  onDescribe?: (description: string) => void;
  initialDescription?: string;
}) {
  const { t, i18n } = useTranslation();
  const [text, setText] = useState(initialDescription ?? '');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Exclude<AgentDraftResult, { ok: true }>['reason'] | null>(null);
  const ran = useRef(false);
  const valid = text.trim().length >= 8;

  const design = async (value = text) => {
    const description = value.trim();
    if (description.length < 8 || busy) return;
    if (!onDraft) return onDescribe?.(description);
    setBusy(true);
    setProblem(null);
    try {
      const r = (await call('draftAgent', {
        body: { description, locale: i18n.language === 'nl' ? 'nl' : 'en' },
      })) as AgentDraftResult;
      if (r.ok) onDraft(r.draft, r.costUsd);
      else setProblem(r.reason);
    } catch {
      setProblem('error');
    } finally {
      setBusy(false);
    }
  };

  // Arriving with a description (e.g. from the chats screen): design it straight away, once.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run only for the initial description
  useEffect(() => {
    if (initialDescription && onDraft && !ran.current) {
      ran.current = true;
      void design(initialDescription);
    }
  }, [initialDescription]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void design();
      }}
      className="flex flex-col gap-2 rounded-xl border border-accent/40 bg-accent/5 p-3"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-fg">
        <IconSparkle size={14} className="text-accent" /> {t('newAgent.designTitle')}
      </div>
      <p className="text-xs text-muted">{t('newAgent.designHint')}</p>
      <Textarea
        rows={3}
        aria-label={t('newAgent.designTitle')}
        value={text}
        maxLength={2000}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void design();
          }
        }}
        placeholder={t('newAgent.designPlaceholder')}
      />
      {problem ? <Banner tone="warn">{t(`newAgent.designUnavailable.${problem}`)}</Banner> : null}
      <div>
        <Button type="submit" variant="primary" loading={busy} disabled={!valid}>
          {busy ? t('newAgent.designing') : t('newAgent.designButton')}
        </Button>
      </div>
    </form>
  );
}

export function TemplateGrid({
  onTemplate,
  showBlank = true,
}: {
  onTemplate: (c: StarterChoice) => void;
  showBlank?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-muted">{t('newAgent.orTemplate')}</p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {AGENT_TEMPLATES.map((x) => (
          <button
            key={x.id}
            type="button"
            onClick={() => onTemplate(x)}
            className="flex items-start gap-3 rounded-xl border border-border p-3 text-left transition-colors hover:bg-hover"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-panel font-mono text-base">
              {x.avatar}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-fg">{x.roleTitle}</span>
              <span className="block text-xs text-muted">{x.description}</span>
            </span>
          </button>
        ))}
        {showBlank ? (
          <button
            type="button"
            onClick={() => onTemplate('blank')}
            className="flex items-start gap-3 rounded-xl border border-dashed border-border p-3 text-left transition-colors hover:bg-hover"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-panel font-mono text-base">
              +
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-fg">{t('newAgent.blank')}</span>
              <span className="block text-xs text-muted">{t('newAgent.blankHint')}</span>
            </span>
          </button>
        ) : null}
      </div>
    </div>
  );
}
